import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { FC } from 'react';
import Map, { MapRef, Marker, Popup } from 'react-map-gl';
import PropertyQuickViewModal from '../modals/PropertyQuickViewModal';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import mapboxgl, { PointLike } from 'mapbox-gl';
import AvmCompMapMarker from '../../property-detail-page/AvmCompMapMarker';
import {
  abandonFocus,
  setFocusedIndex,
} from '../../../redux/state/avmMapSlice';
import {
  AddressType,
  AvmCompForMap,
  PropertyDataProps,
} from '../../../utility/types';
import {
  getMapboxBoundingBox,
  getMapboxPopupOrientationAndOffset,
  moveMapMarkerToLeftEdge,
  POPUP_WIDTH,
  WIDE_MAP,
  Z_INDEX_MAP_POPUP,
} from '../../../utility/mapUtilities';
import {
  SATELLITE_VIEW_STYLE,
  STREET_VIEW_STYLE,
} from '../../../utility/constants';
import { MapButtonContainer } from './helpers/MapButtonContainer';
import { MapButton } from './helpers/MapButton';

export type AvmMapProps = {
  property: PropertyDataProps;
  comps?: AvmCompForMap[];
};

const AvmMap: FC<AvmMapProps> = ({ property, comps }) => {
  const dispatch = useDispatch<AppDispatch>();
  const focusedIndex = useSelector(
    (state: RootState) => state.avmMap.focusedIndex
  );
  const mapKey = useSelector((state: RootState) => state.map.key);
  const markersRef = useRef({});
  const mapRef = useCallback((node: MapRef) => {
    if (node !== null) {
      setMapNode(node);
      setMoveMapToFitPopup(getMapboxBoundingBox(node).width < WIDE_MAP);
    }
  }, []);

  const [mapNode, setMapNode] = useState(null);
  const [isAvmComp, setIsAvmComp] = useState(false);
  const [popupHeight, setPopupHeight] = useState(0);
  const [popupLoading, setPopupLoading] = useState(false);
  const [previousCenter, setPreviousCenter] = useState(null);
  const [isSatelliteView, setIsSatelliteView] = useState(false);
  const [mapResizeNeeded, setMapResizeNeeded] = useState(false);
  const [popupInfo, setPopupInfo] = useState<AddressType>(null);
  const [showActiveComps, setShowActiveComps] = useState(false);
  const [popupOrientation, setPopupOrientation] = useState(null);
  const [moveMapToFitPopup, setMoveMapToFitPopup] = useState(false);
  const [popupOffset, setPopupOffset] = useState<PointLike>([0, 0]);

  const startingCenter = {
    lat: +property?.address?.latitude,
    lng: +property?.address?.longitude,
  };

  useEffect(() => {
    if (popupInfo) {
      const comp = comps?.find(
        (comp) => comp.property.id === popupInfo.property_id
      );
      setIsAvmComp(!!comp);
    }
  }, [comps, popupInfo]);

  useEffect(() => {
    if (popupHeight && popupInfo) {
      setPopupOrientationAndOffset(popupInfo.id);
    }
  }, [popupHeight]);

  const setPopupOrientationAndOffset = (id: string) => {
    let orientation, offset;
    const marker = markersRef.current[id];

    if (moveMapToFitPopup) {
      orientation = 'left';
      offset = [18, 0];
    } else {
      [orientation, offset] = getMapboxPopupOrientationAndOffset(
        marker,
        mapNode,
        popupHeight
      );
    }

    setPopupOrientation(orientation);
    setPopupOffset(offset);
  };

  if (!mapKey) {
    console.error('Mapkey does not exit');
    return null;
  }

  const clearSelection = () => {
    dispatch(abandonFocus());
    dispatch(setFocusedIndex(null));
    setPopupInfo(null);

    if (moveMapToFitPopup) mapNode.setCenter(previousCenter);
  };

  const fitMapToComps = () => {
    mapNode.resize();
    if (comps === undefined || comps === null) {
      return;
    }
    const latitudes = markers.map((marker) => marker.props.latitude);
    const longitudes = markers.map((marker) => marker.props.longitude);
    const sw = new mapboxgl.LngLat(
      Math.min(...longitudes),
      Math.min(...latitudes)
    );
    const ne = new mapboxgl.LngLat(
      Math.max(...longitudes),
      Math.max(...latitudes)
    );
    const bounds = new mapboxgl.LngLatBounds(sw, ne);
    mapNode.fitBounds(bounds, { padding: 50, duration: 0 });
  };

  const getMarkerHtml = (address, index = undefined, isActive = false) => {
    return (
      <Marker
        longitude={address.longitude}
        latitude={address.latitude}
        key={address.id}
        ref={(el) => (markersRef.current[address.id] = el)}
        style={{ zIndex: isActive ? Z_INDEX_MAP_POPUP : 0 }}
        onClick={(e) => {
          e.originalEvent.stopPropagation();
          setPopupInfo(address);

          if (moveMapToFitPopup) {
            moveMapMarkerToLeftEdge(mapNode, address, setPreviousCenter);
          } else {
            setPopupHeight(0);
          }
        }}
      >
        <AvmCompMapMarker
          index={index}
          loading={popupLoading}
          isActive={isActive}
        />
      </Marker>
    );
  };

  const markers = useMemo(() => {
    let activeIndex = 0;
    let inactiveIndex = 0;
    let filteredComps = comps;

    if (!showActiveComps && filteredComps) {
      filteredComps = filteredComps?.filter((comp) => !comp.isActive);
    }

    const markers =
      filteredComps?.map((comp) => {
        const address = comp?.property?.address;
        if (address.latitude && address.longitude) {
          const index = comp.isActive ? activeIndex++ : inactiveIndex++;
          return getMarkerHtml(address, index, comp.isActive);
        }
      }) ?? [].reverse();

    const address = property?.address;
    if (address) {
      markers.push(getMarkerHtml(address));
    }
    return markers;
  }, [comps, focusedIndex, popupLoading, showActiveComps]);

  useEffect(() => {
    if (mapResizeNeeded) {
      fitMapToComps();
      setMapResizeNeeded(false);
    }
  }, [markers]);

  const toggleActiveComps = () => {
    setShowActiveComps(!showActiveComps);
    setMapResizeNeeded(true);
  };

  const toggleSatelliteView = () => {
    const newStyle = isSatelliteView ? STREET_VIEW_STYLE : SATELLITE_VIEW_STYLE;
    mapNode.getMap().setStyle(newStyle);
    setIsSatelliteView(!isSatelliteView);
  };

  return (
    <div className="h-full relative">
      <MapButtonContainer>
        <MapButton onClick={toggleActiveComps}>
          {showActiveComps ? 'Hide Active Comps' : 'Show Active Comps'}
        </MapButton>
        {comps && (
          <MapButton onClick={toggleSatelliteView}>
            {isSatelliteView ? 'Map View' : 'Satellite View'}
          </MapButton>
        )}
      </MapButtonContainer>
      <Map
        ref={mapRef}
        mapboxAccessToken={mapKey}
        initialViewState={{
          longitude: startingCenter.lng,
          latitude: startingCenter.lat,
          zoom: 10,
        }}
        mapStyle={STREET_VIEW_STYLE}
        onLoad={fitMapToComps}
      >
        {markers}
        {popupInfo && (
          <Popup
            longitude={+popupInfo.longitude}
            latitude={+popupInfo.latitude}
            maxWidth={`${POPUP_WIDTH}px`}
            anchor={popupOrientation}
            offset={popupOffset}
            closeButton={false}
            closeOnMove={true}
            style={{
              zIndex: Z_INDEX_MAP_POPUP,
              visibility: popupHeight > 0 ? 'visible' : 'hidden',
            }}
            onClose={clearSelection}
          >
            <PropertyQuickViewModal
              propertyId={popupInfo.property_id}
              setPopupLoading={setPopupLoading}
              isAvmComp={isAvmComp}
              setHeight={setPopupHeight}
            />
          </Popup>
        )}
      </Map>
    </div>
  );
};

export default AvmMap;
